import { ChevronDown, ChevronUp, Database, Loader2, Play, Sparkles } from 'lucide-react'
import { useLayoutEffect, useRef, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type { PlaygroundAnalysis, PlaygroundChunk } from '@/services/api/playground'
import { MarkdownPreview } from './MarkdownPreview'
import { StepStrip, type StepView } from './StepStrip'

interface DataLakeSectionProps {
  analysis: PlaygroundAnalysis | null
  chunks: PlaygroundChunk[]
  status: string
  error: string | null
  running: boolean
  steps: StepView[]
  onRun: () => void
}

export function DataLakeSection({
  analysis,
  chunks,
  status,
  error,
  running,
  steps,
  onRun,
}: DataLakeSectionProps) {
  return (
    <Card className="space-y-4 border-border p-4 md:p-6">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-primary" />
          <h2 className="text-base font-medium text-foreground">1 · DataLake processing</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          In the DataLake, an uploaded file is stored and put through lightweight indexing: its text
          is extracted, split into chunks, and a quick LLM analysis classifies the document and
          summarizes it — making it searchable. No knowledge graph is built. Below is a simulation:
          the same processing steps run, but results are kept only for this demo.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" onClick={onRun} disabled={running} className="h-9 md:h-8">
          {running ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Play className="mr-1.5 h-3.5 w-3.5" />
          )}
          {status === 'done' ? 'Re-run DataLake processing' : 'Run DataLake processing'}
        </Button>
        {status === 'done' && !running && <Badge variant="secondary">Done</Badge>}
      </div>

      {steps.length > 0 && <StepStrip steps={steps} />}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {analysis && (
        <Card className="space-y-3 border-border bg-muted/50 p-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <h3 className="text-sm font-medium text-foreground">File overview (LLM analysis)</h3>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
            <Field label="File type" value={analysis.file_type} />
            <Field label="Document type" value={analysis.document_type} />
            <Field
              label="Confidence"
              value={
                analysis.confidence != null ? `${Math.round(analysis.confidence * 100)}%` : null
              }
            />
          </div>
          {analysis.summary && (
            <div>
              <div className="mb-1 text-xs text-muted-foreground">Summary</div>
              <p className="text-xs leading-relaxed text-foreground">{analysis.summary}</p>
            </div>
          )}
          {analysis.key_findings && analysis.key_findings.length > 0 && (
            <div>
              <div className="mb-1 text-xs text-muted-foreground">Key findings</div>
              <ul className="list-inside list-disc space-y-0.5 text-xs text-foreground">
                {analysis.key_findings.map(f => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      {chunks.length > 0 && (
        <Card className="space-y-2 border-border bg-muted/50 p-4">
          <h3 className="text-sm font-medium text-foreground">
            Indexed content · {chunks.length} markdown {chunks.length === 1 ? 'chunk' : 'chunks'}
          </h3>
          <div className="max-h-[60dvh] space-y-2 overflow-y-auto pr-1 md:max-h-[32rem]">
            {chunks.map((c, i) => (
              <ChunkCard key={i} index={i + 1} content={c.content} />
            ))}
          </div>
        </Card>
      )}
    </Card>
  )
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="rounded-md bg-background p-2">
      <div className="mb-0.5 text-muted-foreground">{label}</div>
      <div className="font-medium text-foreground max-md:break-words">{value || '—'}</div>
    </div>
  )
}

/** A single indexed chunk. Toggles between a rendered markdown **Preview** and
 *  the **Raw** text, collapses to a teaser with a fade, and expands to full
 *  content. The "Show full chunk" toggle only appears when the active view
 *  actually overflows the collapsed height. */
function ChunkCard({ index, content }: { index: number; content: string }) {
  const [expanded, setExpanded] = useState(false)
  const [view, setView] = useState<'preview' | 'raw'>('preview')
  const [overflowing, setOverflowing] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)

  // Re-measure when the view changes (rendered vs raw differ in height). Skip
  // while expanded so the "Show less" affordance stays available. `view` is a
  // dep to trigger re-measure on toggle even though it isn't read in the body.
  // biome-ignore lint/correctness/useExhaustiveDependencies: view drives re-measure
  useLayoutEffect(() => {
    if (expanded) return
    const el = bodyRef.current
    if (el) setOverflowing(el.scrollHeight > el.clientHeight + 1)
  }, [view, expanded])

  const showToggle = overflowing || expanded

  return (
    <div className="overflow-hidden rounded-md border border-border bg-background">
      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-2.5 py-1.5">
        <span className="font-mono text-[10px] font-medium uppercase tracking-wider text-foreground">
          Chunk {index} · {content.length.toLocaleString()} chars
        </span>
        <div className="flex items-center rounded-md border border-border p-0.5">
          {(['preview', 'raw'] as const).map(v => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={`rounded px-2 py-2.5 text-[10px] font-medium capitalize transition-colors md:py-0.5 ${
                view === v
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
      <div ref={bodyRef} className={`relative ${expanded ? '' : 'max-h-28 overflow-hidden'}`}>
        {view === 'preview' ? (
          <MarkdownPreview content={content} />
        ) : (
          <pre className="whitespace-pre-wrap break-words px-2.5 py-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
            {content}
          </pre>
        )}
        {!expanded && overflowing && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-background to-transparent" />
        )}
      </div>
      {showToggle && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="flex w-full items-center justify-center gap-1 border-t border-border/60 py-2.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground md:py-1.5"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-3 w-3" /> Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" /> Show full chunk
            </>
          )}
        </button>
      )}
    </div>
  )
}
